import Timelinelabel from "../../../../../../layout/Timeline/Timelinelabel";
import ProjectCommentTimelineItem from "../ProjectCommentTimelineItem";
import Timeline from "../../../../../../layout/Timeline/Timeline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFile,
  faComment,
  faListCheck,
  faHourglass,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import TicketList from "../../Charts/TicketList";
import TicketChart from "../../Charts/TicketChart";
import HourChart from "../../Charts/HourChart";
import { calculateDoneTicketPercentage } from "@serie3/freelancesplaceapi/Domain/Projects/Calculations";
import ProjectConfig from "@serie3/freelancesplaceapi/Config/Projects/ProjectConfig";
import crudApi from "@serie3/common/api/crudApi";


const getCustomerName = (project) => {
  const contact = project.person || project.customer;
  if (!contact) return null;
  if (Object.prototype.hasOwnProperty.call(contact, "anrede")) {
    return [contact.vorname, contact.name].filter(Boolean).join(" ");
  }
  return [contact.firmenname, contact.rechtsform].filter(Boolean).join(" ") || null;
};

const getTotalProjectHours = async (setTotalHours, projectId) => {
  const response = await crudApi.justGet(ProjectConfig.endpoints.getTotalProjectHours + projectId);
  if(response.status === 200){
    setTotalHours(response.data);
  }
  
}

const StatCard = ({ icon, label, value, gradient, iconColor }) => (
  <div style={{
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    padding: "18px 20px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "16px",
  }}>
    <div style={{
      width: "44px", height: "44px", borderRadius: "12px",
      background: gradient, display: "flex", alignItems: "center",
      justifyContent: "center", color: iconColor, fontSize: "1.1rem", flexShrink: 0,
    }}>
      <FontAwesomeIcon icon={icon} />
    </div>
    <div>
      <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9e9e9e", marginBottom: "2px" }}>
        {label}
      </div>
      <div style={{ fontSize: "1.25rem", fontWeight: 800, color: "#212121" }}>
        {value}
      </div>
    </div>
  </div>
);

const DashCard = ({ title, children, height }) => (
  <div style={{
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
    height: height || "auto",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  }}>
    <div style={{
      padding: "14px 20px",
      borderBottom: "2px solid #f0f0f0",
      fontWeight: 700,
      fontSize: "0.75rem",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: "#424242",
      background: "#f8f9fb",
    }}>
      {title}
    </div>
    <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
      {children}
    </div>
  </div>
);

const Dashboard = ({ project, comments }) => {
  const [lastComment, setLastComment] = useState(null);
  const [ticketsPercentage, setTicketsPercentage] = useState(null);
  const [totalProjectHours, setTotalProjectHours] = useState(0);
  const [hydratedTasks, setHydratedTasks] = useState([]);
  const tickets = project.actions;

  useEffect(() => {
    const doneTickets = calculateDoneTicketPercentage(project.actions);
    setTicketsPercentage(doneTickets);
    setLastComment(comments[0]);
    getTotalProjectHours(setTotalProjectHours, project.id);
    const tmpHydratedTasks = [];
    project.tasks.map((task) => {
      tmpHydratedTasks.push(task.task);
    });
    setHydratedTasks([...tmpHydratedTasks]);
  }, [project, comments]);

  const contact = project.person || project.customer;
  const isPerson = contact && Object.prototype.hasOwnProperty.call(contact, "anrede");
  const customerName = getCustomerName(project);
  const address = contact?.adresse?.[0];
  const initials = customerName
    ? customerName.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
    : null;

  return (
    <>
      {contact && customerName && (
        <div style={{
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          padding: "20px 24px",
          marginBottom: "20px",
          display: "flex",
          alignItems: "flex-start",
          gap: "20px",
        }}>
          {/* Avatar */}
          <div style={{
            width: "52px", height: "52px", borderRadius: "50%",
            background: "linear-gradient(135deg, #1a73e8, #0d47a1)",
            color: "#fff", display: "flex", alignItems: "center",
            justifyContent: "center", fontWeight: 800, fontSize: "1.1rem",
            flexShrink: 0, letterSpacing: "0.02em",
          }}>
            {initials}
          </div>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px", flexWrap: "wrap" }}>
              <span style={{ fontWeight: 800, fontSize: "1.05rem", color: "#212121" }}>
                {customerName}
              </span>
              <span style={{
                fontSize: "0.7rem", fontWeight: 700, padding: "2px 10px",
                borderRadius: "99px", letterSpacing: "0.06em",
                background: isPerson ? "#e8f0fe" : "#e8f5e9",
                color: isPerson ? "#1a73e8" : "#2e7d32",
              }}>
                {isPerson ? "Person" : (contact.rechtsform || "Company")}
              </span>
            </div>

            {isPerson && contact.position && (
              <div style={{ fontSize: "0.82rem", color: "#757575", marginBottom: "6px" }}>
                {contact.position}
              </div>
            )}

            {address && (address.strasse || address.ort) && (
              <div style={{ fontSize: "0.82rem", color: "#9e9e9e", display: "flex", alignItems: "center", gap: "5px" }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                {[address.strasse && `${address.strasse} ${address.hausnummer || ""}`.trim(), address.plz && `${address.plz} ${address.ort || ""}`.trim()].filter(Boolean).join(", ")}
              </div>
            )}

            {(contact.tax_id || contact.tax_number) && (
              <div style={{ fontSize: "0.78rem", color: "#bdbdbd", marginTop: "4px" }}>
                {contact.tax_id && <span>VAT: {contact.tax_id}</span>}
                {contact.tax_id && contact.tax_number && <span style={{ margin: "0 8px" }}>·</span>}
                {contact.tax_number && <span>Tax No.: {contact.tax_number}</span>}
              </div>
            )}
          </div>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: "20px" }}>
        <StatCard icon={faHourglass} label="Hours" value={totalProjectHours + " h"} gradient="linear-gradient(135deg, #e3f2fd, #bbdefb)" iconColor="#1565c0" />
        <StatCard icon={faListCheck} label="Done" value={ticketsPercentage + " %"} gradient="linear-gradient(135deg, #e8f5e9, #c8e6c9)" iconColor="#2e7d32" />
        <StatCard icon={faFile} label="Files" value={project.documents.length} gradient="linear-gradient(135deg, #fff8e1, #ffecb3)" iconColor="#f57c00" />
        <StatCard icon={faComment} label="Comments" value={project.comments.length} gradient="linear-gradient(135deg, #f3e5f5, #e1bee7)" iconColor="#7b1fa2" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
        <DashCard title="Last comment" height="450px">
          <Timeline showAddButton={false}>
            {lastComment ? (
              <>
                <Timelinelabel labeltext={lastComment.date} />
                <ProjectCommentTimelineItem comment={lastComment} />
              </>
            ) : null}
          </Timeline>
        </DashCard>
        <DashCard title="Tickets active" height="450px">
          <TicketList tickets={tickets} />
        </DashCard>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <DashCard title="Hours" height="450px">
          <HourChart tasks={hydratedTasks} />
        </DashCard>
        <DashCard title="Tickets overview" height="450px">
          <TicketChart tickets={tickets} />
        </DashCard>
      </div>
    </>
  );
};

export default Dashboard;
